import { ref } from 'vue'
export interface Note {
  id: string
  title: string
  content: string
  user_id: string
  color: string
  position?: number
  created_at: string
  updated_at: string
}

export interface CalendarEvent {
  id: string
  title: string
  description?: string
  start: string
  end: string
  user_id: string
  allDay?: boolean
  color?: string
  created_at: string
  updated_at: string
}

const DB_NAME = 'calloperator_db'
const DB_VERSION = 2

export const STORES = {
  NOTES: 'notes',
  CALENDAR_EVENTS: 'calendar_events',
} as const

export function useIndexedDB() {
  const db = ref<IDBDatabase | null>(null)
  const isReady = ref(false)

  const initDB = () =>
    new Promise<void>((resolve, reject) => {
      if (db.value) {
        resolve()
        return
      }

      const req = indexedDB.open(DB_NAME, DB_VERSION)

      req.onerror = () => reject(req.error)
      req.onsuccess = () => {
        db.value = req.result
        isReady.value = true
        resolve()
      }

      req.onupgradeneeded = () => {
        const dbInstance = req.result

        // Notas
        if (!dbInstance.objectStoreNames.contains(STORES.NOTES)) {
          const noteStore = dbInstance.createObjectStore(STORES.NOTES, { keyPath: 'id' })
          noteStore.createIndex('idx_title', 'title', { unique: false })
        }

        // Calendario
        if (!dbInstance.objectStoreNames.contains(STORES.CALENDAR_EVENTS)) {
          const eventStore = dbInstance.createObjectStore(STORES.CALENDAR_EVENTS, { keyPath: 'id' })
          eventStore.createIndex('idx_user_id', 'user_id', { unique: false })
          eventStore.createIndex('idx_start', 'start', { unique: false })
        }
      }
    })

  // ===== Notas =====
  const getAllNotes = (): Promise<Note[]> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')
      const tx = db.value.transaction(STORES.NOTES, 'readonly')
      const req = tx.objectStore(STORES.NOTES).getAll()
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })

  const getNoteById = (id: string): Promise<Note | undefined> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')
      const tx = db.value.transaction(STORES.NOTES, 'readonly')
      const req = tx.objectStore(STORES.NOTES).get(id)
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })

  const addNote = (note: Omit<Note, 'id' | 'created_at' | 'updated_at'>): Promise<string> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')
      const now = new Date().toISOString()
      const full: Note = {
        ...note,
        id: crypto.randomUUID(),
        created_at: now,
        updated_at: now,
      }
      const tx = db.value.transaction(STORES.NOTES, 'readwrite')
      const req = tx.objectStore(STORES.NOTES).add(full)
      req.onsuccess = () => resolve(full.id)
      req.onerror = () => reject(req.error)
    })

  const updateNote = (note: Note): Promise<void> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')
      const tx = db.value.transaction(STORES.NOTES, 'readwrite')
      const req = tx
        .objectStore(STORES.NOTES)
        .put({ ...note, updated_at: new Date().toISOString() })
      req.onsuccess = () => resolve()
      req.onerror = () => reject(req.error)
    })

  const removeNote = (id: string): Promise<void> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')
      const tx = db.value.transaction(STORES.NOTES, 'readwrite')
      const req = tx.objectStore(STORES.NOTES).delete(id)
      req.onsuccess = () => resolve()
      req.onerror = () => reject(req.error)
    })

  // ===== Calendario =====
  const getAllEvents = (): Promise<CalendarEvent[]> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')
      const tx = db.value.transaction(STORES.CALENDAR_EVENTS, 'readonly')
      const req = tx.objectStore(STORES.CALENDAR_EVENTS).getAll()
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })

  const getEventById = (id: string): Promise<CalendarEvent | undefined> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')
      const tx = db.value.transaction(STORES.CALENDAR_EVENTS, 'readonly')
      const req = tx.objectStore(STORES.CALENDAR_EVENTS).get(id)
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })

  const addEvent = (
    event: Omit<CalendarEvent, 'id' | 'created_at' | 'updated_at'>,
  ): Promise<string> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')
      const now = new Date().toISOString()
      const full: CalendarEvent = {
        ...event,
        id: crypto.randomUUID(),
        created_at: now,
        updated_at: now,
      }
      const tx = db.value.transaction(STORES.CALENDAR_EVENTS, 'readwrite')
      const req = tx.objectStore(STORES.CALENDAR_EVENTS).add(full)
      req.onsuccess = () => resolve(full.id)
      req.onerror = () => reject(req.error)
    })

  const updateEvent = (event: CalendarEvent): Promise<void> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')
      const tx = db.value.transaction(STORES.CALENDAR_EVENTS, 'readwrite')
      const req = tx
        .objectStore(STORES.CALENDAR_EVENTS)
        .put({ ...event, updated_at: new Date().toISOString() })
      req.onsuccess = () => resolve()
      req.onerror = () => reject(req.error)
    })

  const removeEvent = (id: string): Promise<void> =>
    new Promise((resolve, reject) => {
      if (!db.value) return reject('DB not initialized')
      const tx = db.value.transaction(STORES.CALENDAR_EVENTS, 'readwrite')
      const req = tx.objectStore(STORES.CALENDAR_EVENTS).delete(id)
      req.onsuccess = () => resolve()
      req.onerror = () => reject(req.error)
    })

  return {
    initDB,
    isReady,
    // Notas
    getAllNotes,
    getNoteById,
    addNote,
    updateNote,
    removeNote,
    // Calendario
    getAllEvents,
    getEventById,
    addEvent,
    updateEvent,
    removeEvent,
  }
}
